/**
 * Shared formatting and calculation utilities for pricing components.
 */

import { fmtDollar, fmtPricingDollar, fmtPricingPct, fmtPct as fmtPctBase } from '../../../utils/formatters';

/** Par target on the rate sheet (100.000 points). Highlighted rows are closest to this, not necessarily exact. */
export const PAR_PRICE_TARGET = 100;

export const NEAR_PAR_LABEL = 'Near Par';

export const NEAR_PAR_RATE_LABEL = 'Near-Par Rate';

export const NEAR_PAR_TOOLTIP =
  'Closest adjusted price to 100.000 points on this rate sheet. It may not be exactly 100.000.';

// Re-export central formatters for existing consumers
export { fmtDollar, fmtPricingDollar, fmtPricingPct };

// In pricing contexts, rates ALWAYS use 3 decimal places (6.500%)
export const fmtPct = fmtPricingPct;

// Pricing-specific: 3 decimal places for rates/price
export const fmt = (v, dec = 3) => v != null ? Number(v).toFixed(dec) : '—';
export const fmtPricingPercent = fmtPricingPct;

export const calcMonthlyPI = (rate, amount, termYears) => {
  if (!rate || !amount || !termYears) return null;
  const r = rate / 100 / 12;
  const n = termYears * 12;
  if (r === 0) return amount / n;
  return (amount * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
};

export const calcMonthlyIO = (rate, amount) => {
  if (!rate || !amount) return null;
  return amount * (rate / 100) / 12;
};

// Pastel chip palette with matched foreground colors. Deterministic, small, and
// picked so that adjacent values in a hashed distribution remain visually distinct.
const CHIP_PALETTE = [
  { bg: '#d6eaff', color: '#0d47a1' }, // blue
  { bg: '#d4f4f4', color: '#006064' }, // teal
  { bg: '#ffe0b2', color: '#8a4b00' }, // orange
  { bg: '#e1bee7', color: '#4a148c' }, // purple
  { bg: '#c8e6c9', color: '#1b5e20' }, // green
  { bg: '#f8bbd0', color: '#880e4f' }, // pink
  { bg: '#fff3cd', color: '#7a4f01' }, // amber
  { bg: '#cfd8dc', color: '#263238' }, // slate
  { bg: '#ffccbc', color: '#bf360c' }, // deep orange
  { bg: '#d1c4e9', color: '#311b92' }, // deep purple
];

// Explicit semantic mappings for known product attribute values. Anything not
// listed here falls back to a deterministic hash pick from CHIP_PALETTE, which
// guarantees that different values produce different chip colors.
const CHIP_OVERRIDES = {
  // Product families
  'Non-QM':        { bg: '#d6eaff', color: '#0d47a1' },
  'Conventional':  { bg: '#c8e6c9', color: '#1b5e20' },
  'FHA':           { bg: '#e1bee7', color: '#4a148c' },
  'VA':            { bg: '#ffcdd2', color: '#b71c1c' },
  'USDA':          { bg: '#dcedc8', color: '#33691e' },
  'Jumbo':         { bg: '#d1c4e9', color: '#311b92' },
  // Payment types
  'Principal And Interest': { bg: '#d4f4f4', color: '#006064' },
  'Interest Only':          { bg: '#ffe0b2', color: '#8a4b00' },
};

const hashString = (s) => {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) {
    h = ((h << 5) - h) + s.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
};

/**
 * Returns a `{ bg, color }` pair for a product-attribute chip.
 * Same value always yields the same color; different values yield
 * different colors (via explicit overrides or a hash-based palette pick).
 */
export const getAttributeChipColor = (value) => {
  if (value == null || value === '') return CHIP_PALETTE[0];
  const key = String(value).trim();
  if (CHIP_OVERRIDES[key]) return CHIP_OVERRIDES[key];
  return CHIP_PALETTE[hashString(key) % CHIP_PALETTE.length];
};
